import { Platform } from "expo-modules-core";
import { type ReactNode, useEffect, useId, useMemo } from "react";
import { Modal, type ModalProps, StyleSheet, View } from "react-native";
import { Portal } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ModalBackdrop } from "~/components/layout/modal/ModalBackdrop";
import { Space } from "~/constants/sizing";
import { useModal } from "~/providers/ModalProvider";
import { useTheme } from "~/providers/ThemeProvider";

export type PartialModalProps = {
    readonly name?: string;
    children: ReactNode;
} & ModalProps;

export const PartialModal = ({ children, name, ...modalProps }: PartialModalProps) => {
    const insets = useSafeAreaInsets();
    const { colours } = useTheme();
    const { modalStack, openPartial, closePartial } = useModal();
    const modalId = useId();
    const level = useMemo(() => modalStack.indexOf(modalId), [modalStack, modalId]);
    const zIndex = useMemo(() => level * 2000, [level]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(name): name
    const id = useMemo(() => `${name ?? ""}${modalId}`, [modalId]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(openPartial): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(closePartial): action
    useEffect(() => {
        openPartial(id);
        return () => closePartial(id);
    }, [id]);

    return (
        <Portal>
            <Modal
                {...modalProps}
                animationType="fade"
                statusBarTranslucent={true}
                presentationStyle="overFullScreen"
                style={{
                    zIndex,
                    paddingBottom: insets.bottom + Space.md,
                }}
                transparent={Platform.select({
                    web: true,
                    default: false,
                })}
                backdropColor={colours.backdrop}
            >
                <ModalBackdrop />
                <View style={styles.container}>{children}</View>
            </Modal>
        </Portal>
    );
};

const styles = StyleSheet.create({
    container: {
        position: "absolute",
        inset: 0,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        paddingHorizontal: Space.xs,
        paddingVertical: Space.md,
        width: "100%",
    },
});
